import { useState } from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  ChevronDown,
  Eye,
  GitCompareArrows,
  Maximize2,
  Minus,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DocumentToolbarGroup } from "./DocumentToolbarGroup";
import { toolbarTier } from "./documentToolbarTiers";

const ZOOM_PRESETS = [50, 75, 100, 125, 150] as const;

export type DocumentCompareProps = {
  isCompareMode?: boolean;
  onToggleCompareMode: () => void;
};

export type DocumentViewGroupProps = {
  zoomLevel: number;
  onZoomChange: (level: number) => void;
  onFitToWidth?: () => void;
  minZoom?: number;
  maxZoom?: number;
  step?: number;
  /**
   * Comparison belongs here rather than among the actions: it changes how the
   * document is drawn, not what the reader does with it. Left unset where a
   * viewer has nothing to compare.
   */
  compare?: DocumentCompareProps;
  /** Names the toolbar this group belongs to, e.g. `legislation`. */
  idPrefix: string;
};

const compareLabel = (isCompareMode?: boolean) =>
  isCompareMode ? "Afslut sammenligning" : "Sammenlign versioner";

/**
 * "Vis": how the document is drawn — zoom, fit, and comparison.
 *
 * The step buttons fold into the zoom menu at the medium tier and the whole
 * group folds into one menu below it. Both menus list the same presets; only
 * the folded one carries comparison, so no width offers the same action twice.
 */
export const DocumentViewGroup = ({
  zoomLevel,
  onZoomChange,
  onFitToWidth,
  minZoom = 50,
  maxZoom = 150,
  step = 10,
  compare,
  idPrefix,
}: DocumentViewGroupProps) => {
  const [expandedOpen, setExpandedOpen] = useState(false);
  const [foldedOpen, setFoldedOpen] = useState(false);

  const zoomTo = (level: number) => {
    onZoomChange(level);
    setExpandedOpen(false);
    setFoldedOpen(false);
  };

  const fitToWidth = () => {
    onFitToWidth?.();
    setExpandedOpen(false);
    setFoldedOpen(false);
  };

  const zoomItems = (
    <>
      {ZOOM_PRESETS.map((value) => (
        <DropdownMenuItem
          key={value}
          onClick={() => zoomTo(value)}
          className={cn("cursor-pointer", zoomLevel === value && "bg-accent")}
        >
          {value}%
        </DropdownMenuItem>
      ))}
      {onFitToWidth && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer gap-2"
            onClick={fitToWidth}
          >
            <Maximize2 className="size-4" />
            Tilpas til bredde
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  return (
    <>
      <DocumentToolbarGroup
        label="Vis"
        className={cn("shrink-0", toolbarTier.fromMediumFlex)}
      >
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn("size-7", toolbarTier.fromFullInlineFlex)}
          onClick={() => onZoomChange(Math.max(minZoom, zoomLevel - step))}
          disabled={zoomLevel <= minZoom}
          aria-label="Zoom ud"
          id={`${idPrefix}-zoom-out-btn`}
        >
          <Minus className="size-4" />
        </Button>

        <DropdownMenu open={expandedOpen} onOpenChange={setExpandedOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                "h-7 gap-1 px-2 text-xs font-medium",
                toolbarTier.capLeftBelowFull,
                !compare && toolbarTier.capRightBelowFull
              )}
              id={`${idPrefix}-zoom-trigger`}
              aria-label="Zoomniveau"
            >
              {zoomLevel}%
              <ChevronDown className="size-3.5 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-44">
            {zoomItems}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn("size-7", toolbarTier.fromFullInlineFlex)}
          onClick={() => onZoomChange(Math.min(maxZoom, zoomLevel + step))}
          disabled={zoomLevel >= maxZoom}
          aria-label="Zoom ind"
          id={`${idPrefix}-zoom-in-btn`}
        >
          <Plus className="size-4" />
        </Button>

        {compare && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant={compare.isCompareMode ? "default" : "outline"}
                size="icon"
                className="size-7"
                onClick={compare.onToggleCompareMode}
                id={`${idPrefix}-compare-toggle-btn`}
                aria-pressed={compare.isCompareMode}
                aria-label={compareLabel(compare.isCompareMode)}
              >
                <GitCompareArrows className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{compareLabel(compare.isCompareMode)}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </DocumentToolbarGroup>

      <DocumentToolbarGroup
        label="Vis"
        className={cn("shrink-0", toolbarTier.belowMedium)}
      >
        <DropdownMenu open={foldedOpen} onOpenChange={setFoldedOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1 px-2 text-xs font-medium"
              id={`${idPrefix}-view-menu`}
              aria-label="Vis"
            >
              <Eye className="size-3.5" />
              {zoomLevel}%
              <ChevronDown className="size-3.5 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {zoomItems}
            {compare && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer gap-2"
                  onClick={() => {
                    compare.onToggleCompareMode();
                    setFoldedOpen(false);
                  }}
                  id={`${idPrefix}-compare-toggle-menu-item`}
                >
                  <GitCompareArrows className="size-4" />
                  {compareLabel(compare.isCompareMode)}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </DocumentToolbarGroup>
    </>
  );
};

export default DocumentViewGroup;
